import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Button } from 'react-bootstrap';
import { useDrag, useDrop } from 'react-dnd';
import Slideout from '../../../components/slideout/Slideout';
import { useNotifications } from '../../../notifications';
import {
  createChecklistTemplate,
  updateChecklistTemplate,
} from '../../../api/checklist-queries';
import { ReactComponent as TrashIcon } from '../../../shared/icons/trashcan-icon.svg';
import { ReactComponent as PlusIcon } from '../../../shared/icons/plus-icon.svg';
import { ReactComponent as DragIcon } from '../../../shared/icons/drag.svg';
import SubmitButton from '../../../components/submit-button';
import styles from './ChecklistTemplateSlideout.module.scss';

interface TemplateItem {
  id?: string;
  tempId?: string; // Temporary ID for new items before saving
  content: string;
  displayOrder: number;
  defaultDueOffsetDays?: number | null;
  children?: TemplateItem[];
}

interface ChecklistTemplateSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  template?: {
    id: string;
    name: string;
    description?: string | null;
    items?: TemplateItem[];
  } | null;
  refetch: () => void;
}

interface DraggableItemProps {
  item: TemplateItem;
  index: number;
  isSubmitting: boolean;
  onUpdateItem: (index: number, content: string) => void;
  onMoveItem: (fromIndex: number, toIndex: number) => void;
  onAddSubItem: (parentIndex: number) => void;
  onRemoveItem: (index: number) => void;
  onUpdateSubItem: (parentIndex: number, subIndex: number, content: string) => void;
  onMoveSubItem: (parentIndex: number, fromIndex: number, toIndex: number) => void;
  onRemoveSubItem: (parentIndex: number, subIndex: number) => void;
}

interface DraggableSubItemProps {
  subItem: TemplateItem;
  parentIndex: number;
  subIndex: number;
  maxLength: number;
  isSubmitting: boolean;
  onUpdateSubItem: (parentIndex: number, subIndex: number, content: string) => void;
  onMoveSubItem: (parentIndex: number, fromIndex: number, toIndex: number) => void;
  onRemoveSubItem: (parentIndex: number, subIndex: number) => void;
}

// Draggable Sub-Item Component - Moved outside to prevent re-renders
const DraggableSubItem: React.FC<DraggableSubItemProps> = ({
  subItem,
  parentIndex,
  subIndex,
  maxLength,
  isSubmitting,
  onUpdateSubItem,
  onMoveSubItem,
  onRemoveSubItem,
}) => {
  const [{ isDragging }, drag, preview] = useDrag({
    type: `SUBITEM-${parentIndex}`,
    item: { subIndex },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: `SUBITEM-${parentIndex}`,
    hover: (draggedItem: { subIndex: number }) => {
      if (draggedItem.subIndex !== subIndex) {
        onMoveSubItem(parentIndex, draggedItem.subIndex, subIndex);
        draggedItem.subIndex = subIndex;
      }
    },
  });

  return (
    <div
      ref={(node) => preview(drop(node))}
      className={styles.subItemRow}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className={styles.subItemContent}>
        <div ref={drag} className={styles.dragHandle}>
          <DragIcon />
        </div>
        <Form.Control
          type="text"
          value={subItem.content}
          onChange={(e) => onUpdateSubItem(parentIndex, subIndex, e.target.value)}
          disabled={isSubmitting}
          className={styles.subItemInput}
        />
        <div className={styles.itemActions}>
          <button
            type="button"
            onClick={() => onRemoveSubItem(parentIndex, subIndex)}
            disabled={isSubmitting}
            className={styles.deleteButton}
            title="Delete sub-item"
          >
            <TrashIcon className={styles.deleteIcon} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Draggable Item Component - Moved outside to prevent re-renders
const DraggableItem: React.FC<DraggableItemProps> = ({
  item,
  index,
  isSubmitting,
  onUpdateItem,
  onMoveItem,
  onAddSubItem,
  onRemoveItem,
  onUpdateSubItem,
  onMoveSubItem,
  onRemoveSubItem,
}) => {
  const [{ isDragging }, drag, preview] = useDrag({
    type: 'ITEM',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'ITEM',
    hover: (draggedItem: { index: number }) => {
      if (draggedItem.index !== index) {
        onMoveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <div ref={(node) => preview(drop(node))} className={styles.itemRow} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <div className={styles.itemContent}>
        <div ref={drag} className={styles.dragHandle}>
          <DragIcon />
        </div>
        <Form.Control
          type="text"
          value={item.content}
          onChange={(e) => onUpdateItem(index, e.target.value)}
          disabled={isSubmitting}
          className={styles.itemInput}
        />
        <div className={styles.itemActions}>
          <button
            type="button"
            onClick={() => onAddSubItem(index)}
            disabled={isSubmitting}
            className={styles.addButton}
            title="Add sub-item"
          >
            <PlusIcon className={styles.plusIconSmall} />
          </button>
          <button
            type="button"
            onClick={() => onRemoveItem(index)}
            disabled={isSubmitting}
            className={styles.deleteButton}
            title="Delete item"
          >
            <TrashIcon className={styles.deleteIcon} />
          </button>
        </div>
      </div>

      {/* Render sub-items */}
      {item.children && item.children.length > 0 && (
        <div className={styles.subItemsList}>
          {item.children.map((subItem, subIndex) => (
            <DraggableSubItem
              key={subItem.id || subItem.tempId}
              subItem={subItem}
              parentIndex={index}
              subIndex={subIndex}
              maxLength={item.children!.length}
              isSubmitting={isSubmitting}
              onUpdateSubItem={onUpdateSubItem}
              onMoveSubItem={onMoveSubItem}
              onRemoveSubItem={onRemoveSubItem}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const ChecklistTemplateSlideout: React.FC<ChecklistTemplateSlideoutProps> = ({
  isOpen,
  onClose,
  teamId,
  template,
  refetch,
}) => {
  const { formatMessage } = useIntl();
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState<TemplateItem[]>([]);
  const [newItemContent, setNewItemContent] = useState('');

  const [createTemplate, { loading: creating }] = useMutation(createChecklistTemplate);
  const [updateTemplate, { loading: updating }] = useMutation(updateChecklistTemplate);

  // Counter for generating unique temporary IDs
  const tempIdCounterRef = React.useRef(0);
  const generateTempId = () => {
    tempIdCounterRef.current += 1;
    return `temp-${tempIdCounterRef.current}`;
  };

  const isEditing = !!template;
  const isSubmitting = creating || updating;

  // Helper to ensure items have unique identifiers
  const ensureItemIds = (items: TemplateItem[]): TemplateItem[] => {
    return items.map(item => ({
      ...item,
      tempId: item.id || item.tempId || generateTempId(),
      children: item.children ? ensureItemIds(item.children) : undefined
    }));
  };

  // Initialize form when template changes
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      // Ensure all items have IDs (either from server or generated tempIds)
      setItems(template.items ? ensureItemIds(template.items) : []);
    } else {
      setName('');
      setDescription('');
      setItems([]);
    }
    setNewItemContent('');
  }, [template, isOpen]);

  const handleAddItem = () => {
    if (!newItemContent.trim()) return;

    const newItem: TemplateItem = {
      tempId: generateTempId(),
      content: newItemContent.trim(),
      displayOrder: items.length,
      children: [],
    };

    setItems([...items, newItem]);
    setNewItemContent('');
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleUpdateItem = (index: number, content: string) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], content };
    setItems(updatedItems);
  };

  const handleMoveItem = (fromIndex: number, toIndex: number) => {
    const updatedItems = [...items];
    const [movedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, movedItem);

    // Update display order
    const reorderedItems = updatedItems.map((item, i) => ({
      ...item,
      displayOrder: i
    }));

    setItems(reorderedItems);
  };

  const handleAddSubItem = (parentIndex: number) => {
    const updatedItems = [...items];
    const parentItem = {
      ...updatedItems[parentIndex],
      children: [...(updatedItems[parentIndex].children || [])]
    };

    const newSubItem: TemplateItem = {
      tempId: generateTempId(),
      content: 'New sub-item',
      displayOrder: parentItem.children.length,
    };

    parentItem.children.push(newSubItem);
    updatedItems[parentIndex] = parentItem;
    setItems(updatedItems);
  };

  const handleRemoveSubItem = (parentIndex: number, subIndex: number) => {
    const updatedItems = [...items];
    const parentItem = {
      ...updatedItems[parentIndex],
      children: updatedItems[parentIndex].children?.filter((_, i) => i !== subIndex)
    };
    updatedItems[parentIndex] = parentItem;
    setItems(updatedItems);
  };

  const handleUpdateSubItem = (parentIndex: number, subIndex: number, content: string) => {
    const updatedItems = [...items];
    const parentItem = { ...updatedItems[parentIndex] };

    if (parentItem.children?.[subIndex]) {
      parentItem.children = parentItem.children.map((child, i) =>
        i === subIndex ? { ...child, content } : child
      );
    }

    updatedItems[parentIndex] = parentItem;
    setItems(updatedItems);
  };

  const handleMoveSubItem = (parentIndex: number, fromIndex: number, toIndex: number) => {
    const updatedItems = [...items];
    const parentItem = { ...updatedItems[parentIndex] };
    const children = parentItem.children ? [...parentItem.children] : [];

    const [movedChild] = children.splice(fromIndex, 1);
    children.splice(toIndex, 0, movedChild);

    // Update display order
    parentItem.children = children.map((child, i) => ({
      ...child,
      displayOrder: i
    }));

    updatedItems[parentIndex] = parentItem;
    setItems(updatedItems);
  };

  // Helper to clean tempId from items before submission
  const cleanItemsForSubmission = (items: TemplateItem[]): any[] => {
    return items.map(item => {
      const { tempId, ...itemWithoutTempId } = item;
      return {
        ...itemWithoutTempId,
        children: item.children ? cleanItemsForSubmission(item.children) : undefined
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      notifyError({
        message: {
          id: 'checklist-template.name-required',
          defaultMessage: 'Template name is required',
        },
      });
      return;
    }

    try {
      // Clean tempIds from items before sending to server
      const cleanedItems = cleanItemsForSubmission(items);

      if (isEditing) {
        const result = await updateTemplate({
          variables: {
            id: template.id,
            name: name.trim(),
            description: description.trim() || null,
            items: cleanedItems,
          },
        });

        if (result.data?.updateChecklistTemplate?.errors?.length) {
          notifyError({
            message: {
              id: 'checklist-template.update-error',
              defaultMessage: result.data.updateChecklistTemplate.errors[0] || 'Failed to update template',
            },
          });
          return;
        }

        notifySuccess({
          message: {
            id: 'checklist-template.updated',
            defaultMessage: 'Template updated successfully',
          },
        });
      } else {
        const result = await createTemplate({
          variables: {
            teamId,
            name: name.trim(),
            description: description.trim() || null,
            items: cleanedItems,
          },
        });

        if (result.data?.createChecklistTemplate?.errors?.length) {
          notifyError({
            message: {
              id: 'checklist-template.create-error',
              defaultMessage: result.data.createChecklistTemplate.errors[0] || 'Failed to create template',
            },
          });
          return;
        }

        notifySuccess({
          message: {
            id: 'checklist-template.created',
            defaultMessage: 'Template created successfully',
          },
        });
      }

      await refetch();
      onClose();
    } catch (error) {
      notifyError({
        message: {
          id: 'checklist-template.submit-error',
          defaultMessage: 'An error occurred. Please try again.',
        },
      });
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setItems([]);
    setNewItemContent('');
    onClose();
  };

  return (
    <Slideout isOpen={isOpen} onClose={handleClose} title={isEditing ? 'Edit Checklist Template' : 'Create Checklist Template'} width="medium">
      <div className={styles.slideoutContent}>
        <Form onSubmit={handleSubmit}>
          {/* Template Name */}
          <Form.Group className="mb-3">
            <Form.Label>
              <FormattedMessage id="checklist-template.name" defaultMessage="Template Name" />
              <span className={styles.required}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={formatMessage({ id: 'checklist-template.name-placeholder', defaultMessage: 'Enter template name' })}
              required
              disabled={isSubmitting}
            />
          </Form.Group>

          {/* Description */}
          <Form.Group className="mb-4">
            <Form.Label>
              <FormattedMessage id="checklist-template.description" defaultMessage="Description" />
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={formatMessage({ id: 'checklist-template.description-placeholder', defaultMessage: 'Enter description (optional)' })}
              disabled={isSubmitting}
            />
          </Form.Group>

          {/* Checklist Items */}
          <div className={styles.itemsSection}>
            <h4 className={styles.itemsTitle}>
              <FormattedMessage id="checklist-template.items" defaultMessage="Checklist Items" />
            </h4>

            {/* Add New Item */}
            <div className={styles.addItemSection}>
              <Form.Control
                type="text"
                value={newItemContent}
                onChange={(e) => setNewItemContent(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddItem();
                  }
                }}
                placeholder={formatMessage({ id: 'checklist-template.add-item-placeholder', defaultMessage: 'Add a new item...' })}
                disabled={isSubmitting}
                className={styles.addItemInput}
              />
              <Button variant="primary" onClick={handleAddItem} disabled={!newItemContent.trim() || isSubmitting} className={styles.addItemButton}>
                <PlusIcon className={styles.plusIcon} />
                <FormattedMessage id="checklist-template.add-item" defaultMessage="Add Item" />
              </Button>
            </div>

            {items.length === 0 ? (
              <div className={styles.emptyItems}>
                <FormattedMessage id="checklist-template.no-items" defaultMessage="No items yet. Add items above." />
              </div>
            ) : (
              <div className={styles.itemsList}>
                {items.map((item, index) => (
                  <DraggableItem
                    key={item.id || item.tempId}
                    item={item}
                    index={index}
                    isSubmitting={isSubmitting}
                    onUpdateItem={handleUpdateItem}
                    onMoveItem={handleMoveItem}
                    onAddSubItem={handleAddSubItem}
                    onRemoveItem={handleRemoveItem}
                    onUpdateSubItem={handleUpdateSubItem}
                    onMoveSubItem={handleMoveSubItem}
                    onRemoveSubItem={handleRemoveSubItem}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className={styles.slideoutActions}>
            <Button variant="danger" onClick={handleClose} disabled={isSubmitting}>
              <FormattedMessage id="cta.cancel" defaultMessage="Cancel" />
            </Button>
            <SubmitButton
              variant="primary"
              label={isEditing ? formatMessage({ id: 'cta.save', defaultMessage: 'Save' }) : formatMessage({ id: 'cta.create', defaultMessage: 'Create' })}
              disabled={isSubmitting || !name.trim()}
              isSubmitting={isSubmitting}
            />
          </div>
        </Form>
      </div>
    </Slideout>
  );
};

export default ChecklistTemplateSlideout;
